/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module AlertModule
 */

import { Collection } from 'ajs/modules/data-model/factories/collection.factory';
import { Alert } from 'object-types';
import { AlertItem } from 'ng/modules/items/factories';
import { DevLoggerService } from 'ng/modules/core/services/dev-logger.service';
import * as moment from 'moment';

const allDataSources = {
    list: {
        source: 'ListCollDataSource',
        transformer: 'AlertListDataTransformer',
        transport: 'ListDataTransport',
        fields: ['config'],
    },
};

/**
 * @description Collection for All Alerts component
 * @author Shahab Hashmi
 */

export class AlertCollection extends Collection {
    public static ngDependencies = [AlertItem];

    private readonly devLoggerService: DevLoggerService;

    constructor(args = {}) {
        const extendedArgs = {
            objectName: 'alert',
            defaultDataSources: 'list',
            objectType: Alert,
            allDataSources,
            isStatic: true,
            searchFields: [
                'name',
                'obj_name',
            ],
            ...args,
        };

        super(extendedArgs);

        this.itemClass_ = this.getNgDependency(AlertItem);
    }

    /**
     * Set range parameters.
     */
    public loadRange(tBegin: Date, tEnd: Date): Promise<any> {
        let errMsg;
        let promise;

        if (!tBegin || !tEnd) {
            errMsg = 'AlertCollection.loadRange failed: begin or end is not passed';
            this.devLoggerService.error(errMsg);
            promise = this.$q.reject(errMsg);
        } else {
            // Remove ts_duration from previous loadDuration requests
            // calculate beginning and end using timeDifference

            this.setParams({
                ts_duration: undefined,
                ts_start: moment.utc(tBegin).format('YYYY-MM-DDTHH:mm:ss'),
                ts_end: moment.utc(tEnd).format('YYYY-MM-DDTHH:mm:ss'),
            });

            promise = this.load(undefined, true);
        }

        return promise;
    }

    /**
     * Set duration in seconds based on step and limit,
     * and handle UNIX timestamp conversions.
     */
    public loadDuration(step: number, limit: number): Promise<any> {
        let errMsg;
        let promise;

        if (!step || !limit) {
            errMsg = 'AlertCollection.loadDuration failed: step or limit is not passed';
            promise = this.$q.reject(errMsg);
            this.devLoggerService.error(errMsg);
        } else {
            // Ensure step and limit are valid before calculating duration
            const currentTimeUnix = Math.floor(Date.now() / 1000);
            const durationInSeconds = step * limit; // Calculate total duration in seconds

            // Calculate the start time (UNIX timestamp) based on duration and current time
            const tsStartUnix = currentTimeUnix - durationInSeconds;

            // Convert the UNIX timestamp to UTC format for ts_start
            const tsStartUtc = moment.utc(tsStartUnix * 1000).format('YYYY-MM-DDTHH:mm:ss');
            const tsEndUtc = moment.utc(currentTimeUnix * 1000).format('YYYY-MM-DDTHH:mm:ss');

            // Remove ts_start, ts_end from previous loadRange requests
            // and set the calculated duration and start time
            this.setParams({
                ts_duration: durationInSeconds,
                ts_start: tsStartUtc, // Start time in UTC format
                ts_end: tsEndUtc, // Current time as end time in UTC format
            });

            promise = this.load(undefined, true);
        }

        return promise;
    }

    /**
     * Alerts API doesn't support search yet, but we want to provide ability to return only
     * one alert with passed id. Search string should have an appropriate format like
     * alertId=<uuid>.
     * @override
     */
    public setSearch(str: string): boolean {
        const searchByUuidExp = /alertId\s*=\s*['"]?([\w-]+)['"]?/i;
        const prevUuidParamValue = this.getParams('uuid');

        let res = false;
        let regExpRes;

        // special case for uuid access
        if (
            str && typeof str === 'string' &&
            // eslint-disable-next-line no-cond-assign
            (regExpRes = searchByUuidExp.exec(str))
        ) {
            if (regExpRes[1] !== prevUuidParamValue) {
                this.setParams({ uuid: regExpRes[1] });
                res = true;
            }

            // empty any search string we used to have
            res = super.setSearch(undefined) || res;
        } else { // regular search
            this.setParams({ uuid: undefined });

            if (prevUuidParamValue) {
                res = true;
            }

            res = super.setSearch(str) || res;
        }

        return res;
    }
}

export type TAlertCollection = typeof AlertCollection;
